import {inject} from '@angular/core';
import {Routes} from '@angular/router';
import {authGuard} from './shared/guards/auth/auth.guard';
import {libraryGuard} from './shared/guards/library/library.guard';
import {adminGuard} from './shared/guards/admin/admin.guard';
import {gccCollectionGuard} from './shared/guards/gcc-collection/gcc-collection.guard';
import {answersGuard} from './shared/guards/answers/answers.guard';
import {InsurersResolver} from './shared/resolvers/insurers/insurers.resolver';
import {sobradoConnectGuard} from './shared/guards/sobrado-connect/sobrado-connect.guard';
import {defaultFilterGuard} from './shared/guards/default-filter/default-filter.guard';
import {NogaIndustriesResolver} from './shared/resolvers/noga-industries/noga-industries.resolver';
import {crmGuard} from './shared/guards/crm/crm.guard';
import {applicationAdminGuard} from './shared/guards/application-admin/application-admin.guard';
import {sobradoUnderwritingGuard} from './shared/guards/sobrado-underwriting/sobrado-underwriting.guard';
import {LobsResolver} from './shared/resolvers/lobs/lobs.resolver';
import {Links} from './shared/common/constants/links';
import {OidcCallbackComponent} from './modules/oidc-callback/oidc-callback.component';
import {ClearSessionComponent} from './shared/components/storage/clear-session/clear-session.component';
import {brokerGuard} from './shared/guards/broker/broker.guard';
import {nonPlatformMemberGuard} from './shared/guards/non-platform-member/non-platform-member.guard';
import {FeatureAclService} from './shared/services/infrastructure/auth/acl/feature-acl/feature-acl.service';
import {UserAclService} from './shared/services/infrastructure/auth/acl/user-acl/user-acl.service';

export const APP_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const featureAclService = inject(FeatureAclService);
      const userAclService = inject(UserAclService);

      if (featureAclService.isCngEnabled() && userAclService.isBrokerDashboardEligible()) {
        return Links.BROKER_CALL_DASHBOARD_LINK_URL;
      }
      return Links.LIBRARY_LINK_URL;
    },
  },
  {
    path: Links.LIBRARY_LINK_URL,
    loadChildren: () => import('./modules/library-domain/library-iframe/library/library.routes')
      .then(m => m.LIBRARY_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, libraryGuard],
    resolve: {
      data: () => inject(InsurersResolver).resolve(),
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.COLLECTION_LINK_URL,
    loadChildren: () => import('./modules/collection/collection.routes')
      .then(m => m.COLLECTION_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, gccCollectionGuard],
  },
  {
    path: Links.GCC_LINK_URL,
    loadChildren: () => import('./modules/gcc/gcc.routes')
      .then(m => m.GCC_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
  },
  {
    path: Links.ANSWERS_LINK_URL,
    loadChildren: () => import('./modules/answers/answers.routes')
      .then(m => m.ANSWERS_ROUTES),
    canActivate: [authGuard, answersGuard],
    resolve: {
      insurers: () => inject(InsurersResolver).resolve(),
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.MIS_LINK_URL,
    loadChildren: () => import('./modules/mis/mis.routes')
      .then(m => m.MIS_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
    resolve: {
      insurers: () => inject(InsurersResolver).resolve(),
      nogaIndustries: () => inject(NogaIndustriesResolver).resolve(),
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.SOBRADO_CONNECT_LINK_URL,
    loadChildren: () => import('./modules/sobrado-connect/sobrado-connect.routes')
      .then(m => m.SOBRADO_CONNECT_ROUTES),
    canActivate: [authGuard, sobradoConnectGuard],
    resolve: {
      data: () => inject(InsurersResolver).resolve(),
    },
  },
  {
    path: Links.PRINT_LINK_URL,
    loadChildren: () => import('./modules/print/print.routes')
      .then(m => m.PRINT_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
  },
  {
    path: Links.DEFAULT_FILTERS_LINK_URL,
    loadChildren: () => import('./modules/library-domain/default-filters/default-filters.routes')
      .then(m => m.DEFAULT_FILTERS_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, defaultFilterGuard],
  },
  {
    path: Links.CRM_LINK_URL,
    loadChildren: () => import('./modules/crm/crm.routes')
      .then(m => m.CRM_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, crmGuard],
    resolve: {
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.LINK_CALL_API_PROCESS,
    loadChildren: () => import('./modules/call-api-process/call-api-process.routes')
      .then(m => m.CALL_API_PROCESS_ROUTES),
    canActivate: [authGuard, brokerGuard],
  },
  {
    path: Links.SETTINGS_LINK_URL,
    loadChildren: () => import('./modules/settings/settings.routes')
      .then(m => m.SETTINGS_ROUTES),
    canActivate: [authGuard, applicationAdminGuard],
  },
  {
    path: Links.SOBRADO_UNDERWRITING_LINK_URL,
    loadChildren: () => import('./modules/sobrado-underwriting/sobrado-underwriting.routes')
      .then(m => m.SOBRADO_UNDERWRITING_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, sobradoUnderwritingGuard],
    resolve: {
      insurers: () => inject(InsurersResolver).resolve(),
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.ADMIN_LINK_URL,
    loadChildren: () => import('./modules/admin/admin.routes')
      .then(m => m.ADMIN_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, adminGuard],
    resolve: {
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.IFRAME_LINK_URL,
    loadChildren: () => import('./modules/library-domain/library-iframe/iframe/iframe.routes')
      .then(m => m.IFRAME_ROUTES),
  },
  {
    path: Links.SHARED_LINK_URL,
    loadChildren: () => import('./modules/library-domain/shared-link/shared-link.routes')
      .then(m => m.SHARED_LINK_ROUTES),
  },
  {
    path: Links.OPEN_COMPONENTS_LINK_URL,
    loadChildren: () => import('./modules/library-domain/open-components/open-components.routes')
      .then(m => m.OPEN_COMPONENTS_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
  },
  {
    path: Links.SBS_LINK_URL,
    loadChildren: () => import('./modules/sbs-domain/sbs-domain.routes')
      .then(m => m.SBS_DOMAIN_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
    data: {preload: true},
  },
  {
    path: Links.BROKER_LINK_URL,
    loadChildren: () => import('./modules/broker/broker.routes')
      .then(m => m.BROKER_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard, brokerGuard],
    data: {preload: true},
    resolve: {
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.INSURER_LINK_URL,
    loadChildren: () => import('./modules/insurer/insurer.routes')
      .then(m => m.INSURER_ROUTES),
    canActivate: [nonPlatformMemberGuard],
  },
  {
    path: Links.POLICY_LINK_URL,
    loadChildren: () => import('./modules/policy/policy.routes')
      .then(m => m.POLICY_ROUTES),
    canActivate: [nonPlatformMemberGuard],
  },
  {
    path: Links.CALL_TEMPLATE_URL,
    loadChildren: () => import('./modules/call-template/call-template.routes')
      .then(m => m.CALL_TEMPLATE_ROUTES),
    canActivate: [authGuard],
    resolve: {
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.PPL_LINK_URL,
    loadChildren: () => import('./modules/pension-participation-law/pension-participation-law.routes')
      .then(m => m.PENSION_PARTICIPATION_LAW_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
  },
  {
    path: Links.CUSTOMIZED_EXCEL_LINK_URL,
    loadChildren: () => import('./modules/customized-excel/customized-excel.routes')
      .then(m => m.CUSTOMIZED_EXCEL_ROUTES),
    canActivate: [authGuard],
    resolve: {
      lobs: () => inject(LobsResolver).resolve(),
    },
  },
  {
    path: Links.TEST_LINK_URL,
    loadChildren: () => import('./modules/test-page/test-page.routes')
      .then(m => m.TEST_PAGE_ROUTES),
    canActivate: [authGuard, nonPlatformMemberGuard],
  },
  {
    path: Links.LINK_OIDC_CALLBACK,
    component: OidcCallbackComponent,
  },
  {
    path: Links.ERROR_LINK_URL,
    loadChildren: () => import('./modules/error-pages/error-pages.routes')
      .then(m => m.ERROR_PAGES_ROUTES),
  },
  {
    path: Links.CLEAR_SESSION_URL,
    component: ClearSessionComponent,
  },
  {
    path: '**',
    redirectTo: Links.ERROR_LINK_URL,
  },
];
